import { userPlaceholder } from 'assets/icons';
import NotFound from 'common/NotFound';
import useDebounce from 'common/UseDebounce';
import useFullPageLoader from 'common/UseFullPageLoader';
import { SearchBar } from 'components/form';
import { CustomModal } from 'components/modals/ModalBox';
import React, { useEffect, useState } from 'react'
import { GiMedicines } from 'react-icons/gi';
import Pagination from 'react-js-pagination';
import { useSearchParams } from 'react-router-dom';
import { medicineListApi } from 'services/user';
import findSerialNumber from 'utils/findSerialNumber';
import { toastMessage } from 'utils/toastMessage';
import MedicineType from './MedicineType';

const ProductList = ({ show, close, userId, addProduct, setAddProduct = () => { } }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [medicineTypeModal, setMedicineTypeModal] = useState({ modal: false, product: null });
    const [loader, onShow, onHide] = useFullPageLoader();
    const [productList, setProductList] = useState([]);
    const activePage = +searchParams.get("page") || 1;
    const limit = +searchParams.get("limit") || 10;
    const [total, setTotal] = useState(0);
    const [selectedVariant, setSelectedVariant] = useState({});
    const search = searchParams.get("search") || "";
    const debouncedValue = useDebounce(search, 300);
    const [imagePreview, setImagePreview] = useState({ url: '', modal: false });

    const getVariantPrice = (variant = {}) => {
        const finalAmount = Number(variant?.finalAmount);
        if (Number.isFinite(finalAmount) && finalAmount > 0) return finalAmount;
        return variant?.mrp;
    };

    const formatAmount = (amount) => {
        const value = Number(amount);
        return Number.isFinite(value) ? `₹${value.toFixed(value % 1 === 0 ? 0 : 2)}` : 'N/A';
    };

    const isVariantAdded = (productId, variantId) => (
        addProduct?.some((item) => item?._id === productId && item?.variant?._id === variantId)
    );

    const setSearchFun = (searchTxt) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("page", 1);
        newParams.set("search", searchTxt);
        setSearchParams(newParams);
    };

    const handlePageChange = (event) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("page", event);
        setSearchParams(newParams);
    };

    const handleImagePrev = (url) => {
        if (url) setImagePreview({ url: url, modal: true });
    };

    const fetchMedicineList = async () => {
        const query = { search: debouncedValue || '', page: activePage, limit: limit };
        onShow();
        try {
            const res = await medicineListApi(userId, query);
            const { code, data } = res?.data;
            if (code === 200) {
                setTotal(data?.total || 0);
                setProductList(data?.data || []);
            }
        } catch (error) {
            console.log(error);
        } finally {
            onHide();
        }
    };

    const closeModal = () => {
        close();
        setSelectedVariant({});
        searchParams.delete('page');
        setSearchParams(searchParams);
    };

    useEffect(() => {
        if (show) fetchMedicineList();
    }, [activePage, show, debouncedValue]);

    useEffect(() => {
        if (!debouncedValue) searchParams.delete('search');
        if (activePage === 1) searchParams.delete('page');
        setSearchParams(searchParams);
    }, [debouncedValue, activePage]);

    // ---- open Strip/Loose modal instead of directly adding ----
    const handleOpenMedicineType = (data) => {
        const variantId = selectedVariant[data?._id] || data?.variant?.[0]?._id;
        if (isVariantAdded(data?._id, variantId)) return;
        setMedicineTypeModal({ modal: true, product: data });
    };

    // ---- called back from MedicineType after Strip/Loose selected ----
    const handleAddWithType = (product, type, quantity) => {
        const variantId = selectedVariant[product?._id] || product?.variant?.[0]?._id;
        const variant = product?.variant?.find((v) => v._id === variantId) || product?.variant?.[0];

        if (!variant) {
            toastMessage('Variant not found', 'error');
            return;
        }

        setAddProduct((prev) => {
            const exists = prev.some(
                (item) => item._id === product._id && item?.variant?._id === variant?._id
            );
            if (exists) return prev;

            return [
                ...prev,
                {
                    ...product,
                    variant,
                    itemType: type,                                         // 'STRIP' or 'LOOSE'
                    quantity: type === 'LOOSE' ? Number(quantity) || 1 : 1, // initial quantity
                },
            ];
        });

        setMedicineTypeModal({ modal: false, product: null });
        closeModal();
    };

    return (
        <CustomModal
            className={"add_model"}
            show={show}
            handleClose={closeModal}
            size='xl'
        >
            <div className="user-management-container">
                <div className="page_title has_child">
                    <h3>Products List</h3>
                </div>
                <div className="search_wrapper-option-selector">
                    <SearchBar
                        onKeyDown={(e) => {
                            const allowedKeysRegex = /^[a-zA-Z0-9\- ]$/;
                            const controlKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab", "Escape"];
                            if (allowedKeysRegex.test(e.key) || controlKeys.includes(e.key)) return;
                            e.preventDefault();
                        }}
                        placeholder={"Search..."}
                        onChange={(e) => setSearchFun(e.target.value)}
                        value={search}
                    />
                </div>

                <div className="card">
                    {loader}
                    <div className="table-container">
                        <table className="table custom_table">
                            <thead>
                                <tr>
                                    <th className="nowrap text-center">S.No</th>
                                    <th className="nowrap text-center">Image</th>
                                    <th className="nowrap text-center">Name</th>
                                    <th className="nowrap text-center">Type</th>
                                    <th className="nowrap text-center">Description</th>
                                    <th className="nowrap text-center">MRP</th>
                                    <th className="nowrap text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productList.length > 0 ? (
                                    productList?.map((data, index) => (
                                        <tr key={index}>
                                            <td className="nowrap text-center">
                                                {findSerialNumber(index, activePage, limit)}
                                            </td>
                                            <td className="nowrap text-center">
                                                <div className="td-img">
                                                    <img
                                                        src={data?.image?.[0] || userPlaceholder}
                                                        alt={data?.name}
                                                        onClick={() => handleImagePrev(data?.image?.[0])}
                                                    />
                                                </div>
                                            </td>
                                            <td className="nowrap text-center">{data?.name || "N/A"}</td>
                                            <td className="nowrap text-center">{data?.type || "N/A"}</td>
                                            <td className="nowrap text-center">
                                                {data?.description
                                                    ? data.description.replace(/<[^>]+>/g, '').slice(0, 30) +
                                                    (data.description.length > 25 ? '...' : '')
                                                    : "N/A"}
                                            </td>
                                            <td className="nowrap text-center">
                                                {data?.variant?.length > 0
                                                    ? data?.variant.map((item, idx) => (
                                                        <div key={idx}>{formatAmount(getVariantPrice(item))}</div>
                                                    ))
                                                    : "N/A"}
                                            </td>
                                            <td className="nowrap text-center">
                                                <div className="button-container">
                                                    <button
                                                        type="button"
                                                        className="button"
                                                        disabled={isVariantAdded(
                                                            data?._id,
                                                            selectedVariant[data?._id] || data?.variant?.[0]?._id
                                                        )}
                                                        onClick={() => handleOpenMedicineType(data)}
                                                    >
                                                        {isVariantAdded(
                                                            data?._id,
                                                            selectedVariant[data?._id] || data?.variant?.[0]?._id
                                                        )
                                                            ? "Added"
                                                            : "Add Product"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <NotFound
                                        cols={7}
                                        msg={"Medicines data not found"}
                                        onHide={onHide}
                                        icon={<GiMedicines />}
                                    />
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* image preview */}
                <CustomModal
                    className={"sm image_preview_modal"}
                    show={imagePreview?.modal}
                    handleClose={() => setImagePreview({ url: '', modal: false })}
                >
                    <div className="image_preview">
                        <img src={imagePreview?.url || userPlaceholder} alt="preview" />
                    </div>
                </CustomModal>

                {/* pagination */}
                {total > limit && (
                    <div className="pagination-wrapper">
                        <div className="Pagination">
                            <Pagination
                                activePage={activePage}
                                previousLabel={"previous"}
                                nextLabel={"next"}
                                itemsCountPerPage={limit}
                                totalItemsCount={total}
                                pageRangeDisplayed={5}
                                onChange={handlePageChange}
                                hideDisabled={true}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* ---- Strip / Loose selection modal ---- */}
            <MedicineType
                show={medicineTypeModal?.modal}
                close={() => setMedicineTypeModal({ modal: false, product: null })}
                data={medicineTypeModal?.product}
                onAdd={handleAddWithType}
            />
        </CustomModal>
    );
};

export default ProductList;